import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowRight } from 'lucide-react';

export default function Footer() {
  const quickLinks = [
    { to: '/explore', label: 'Explore Destinations' },
    { to: '/packages', label: 'Travel Packages' },
    { to: '/visa', label: 'Visa Services' },
    { to: '/car-rental', label: 'Car Rental' },
    { to: '/tickets', label: 'Tickets & Activities' },
    { to: '/airport-transfer', label: 'Airport Transfers' },
    { to: '/about', label: 'About Us' },
    { to: '/contact', label: 'Contact Us' },
  ];

  return (
    <footer data-testid="footer" className="bg-navy text-white">
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="w-6 h-6 text-gold" />
              <span className="font-heading text-xl font-bold">
                BEYOND <span className="text-gold">HORIZONS</span>
              </span>
            </div>
            <p className="font-body text-sm text-gray-400 leading-relaxed mb-6">
              Where every journey begins with a dream and ends with unforgettable memories. Your trusted travel partner since 2020.
            </p>
            <div className="flex gap-3">
              {['facebook', 'instagram', 'twitter', 'youtube'].map((s) => (
                <a key={s} href="#" className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-gold transition-colors text-sm font-body capitalize">
                  {s[0].toUpperCase()}
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5 text-gold">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="font-body text-sm text-gray-400 hover:text-gold transition-colors flex items-center gap-2">
                    <ArrowRight className="w-3 h-3" /> {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5 text-gold">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-gold mt-1 shrink-0" />
                <div className="font-body text-sm text-gray-400">
                  <a href="tel:+919179992213" className="hover:text-gold transition-colors block">+91 9179992213</a>
                  <a href="tel:+917999017544" className="hover:text-gold transition-colors block">+91 7999017544</a>
                  <span className="text-xs text-gray-500">(Available on WhatsApp)</span>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-gold mt-1 shrink-0" />
                <a href="mailto:admin@beyond-horizon.co.in" className="font-body text-sm text-gray-400 hover:text-gold transition-colors">
                  admin@beyond-horizon.co.in
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold mt-1 shrink-0" />
                <span className="font-body text-sm text-gray-400">
                  2nd Floor Dream Towers, Kushkal Pur Chowk, Raipur — 492001
                </span>
              </li>
            </ul>
          </div>

          {/* WhatsApp CTA */}
          <div>
            <h4 className="font-heading text-lg font-semibold mb-5 text-gold">Get in Touch</h4>
            <p className="font-body text-sm text-gray-400 mb-4">
              Ready to plan your dream trip? Chat with us on WhatsApp for instant assistance.
            </p>
            <a
              href="https://wa.me/919179992213?text=Hi!%20I'm%20interested%20in%20planning%20a%20trip%20with%20Beyond%20Horizons."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-whatsapp-btn"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white rounded-full px-6 py-3 text-sm font-body font-medium transition-all hover:-translate-y-0.5"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              Chat on WhatsApp
            </a>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-body text-xs text-gray-500">
            &copy; {new Date().getFullYear()} Beyond Horizons. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="font-body text-xs text-gray-500 hover:text-gold transition-colors">Privacy Policy</a>
            <a href="#" className="font-body text-xs text-gray-500 hover:text-gold transition-colors">Terms of Service</a>
            <a href="#" className="font-body text-xs text-gray-500 hover:text-gold transition-colors">Refund Policy</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
